import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

const BetaSignupContext = createContext();

export const useBetaSignup = () => {
  const context = useContext(BetaSignupContext);
  if (!context) {
    throw new Error('useBetaSignup must be used within a BetaSignupProvider');
  }
  return context;
};

export const BetaSignupProvider = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasTriggeredScroll, setHasTriggeredScroll] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Check for referral code in URL on mount and route changes
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      // Auto-open modal when coming from a referral link
      setIsModalOpen(true);
    }
  }, [searchParams]);

  // Handle /r/:code referral routes
  useEffect(() => {
    if (location.pathname.startsWith('/r/')) {
      const code = location.pathname.replace('/r/', '');
      if (code) {
        setReferralCode(code);
        setIsModalOpen(true);
      }
    }
  }, [location.pathname]);

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Scroll trigger - fires once when user scrolls past 55% of homepage
  const handleScroll = useCallback(() => {
    if (hasTriggeredScroll) return;
    
    const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
    
    if (scrollPercent >= 55 && window.location.pathname === '/') {
      setHasTriggeredScroll(true);
      openModal();
    }
  }, [hasTriggeredScroll, openModal]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Reset scroll trigger when navigating away from homepage
  useEffect(() => {
    if (window.location.pathname !== '/') {
      setHasTriggeredScroll(false);
    }
  }, []);

  const value = {
    isModalOpen,
    openModal,
    closeModal,
    hasTriggeredScroll,
    referralCode,
    setReferralCode
  };

  return (
    <BetaSignupContext.Provider value={value}>
      {children}
    </BetaSignupContext.Provider>
  );
};
